import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  model,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent,
  DateOptions,
  FormComponent, InputComponent, InputOptions,
  ModalComponent,
  ModalOptions,
} from '@shared/components';
import { DropdownComponent, DropdownOptions } from '@shared/components/dropdown/dropdown.component';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { HttpHeaders } from '@angular/common/http';
import { NgOptimizedImage } from '@angular/common';
import { APIService } from '@core/services';
import { PatientService } from '@core/services/patient.service';
import {HTTPMethods, InputTypes, ModalTypes} from '@core/enums';
import { Coding } from '@core/classes/other/coding';
import { DefaultSearchComponent } from '@shared/components/dropdown/search/default-search/default-search.component';
import { MedicalReport } from '@core/classes/tables/medical-report';
import { assets } from '@core/constants';
import { TranslatePipe } from '@ngx-translate/core';
import moment from 'moment/moment';
import {checkDate} from '@core/helpers';
import { decodeToken } from '@core/helpers/util';

@Component({
  selector: 'ncp-add-medical-report',
  imports: [
    ButtonComponent,
    ModalComponent,
    ReactiveFormsModule,
    DropdownComponent,
    DateComponent,
    NgOptimizedImage,
    TranslatePipe,
    InputComponent,
  ],
  templateUrl: './add-medical-report.component.html',
  styleUrls: ['./add-medical-report.component.scss'],
})
export class AddMedicalReportComponent implements OnInit {
  @ViewChild('mod')
  public mod!: ModalComponent;

  @ViewChild('frm')
  protected formEl?: NgForm;

  @ViewChild('addMedicalReportButton')
  public addMedicalReportButton!: ButtonComponent;

  @ViewChild('medicalCenterDD')
  public medicalCenterDD!: DropdownComponent<any>;

  @ViewChild('specialtyDD')
  public specialtyDD!: DropdownComponent<Coding>;

  @ViewChild('hospitalizationDD')
  public hospitalizationDD!: DropdownComponent<Coding>;

  @ViewChild('onsetDate')
  public onsetDate!: DateComponent;

  @ViewChild('documentFileInput')
  protected documentFileInput?: ElementRef<HTMLInputElement>;

  protected apiService = inject(APIService);
  protected patientService = inject(PatientService);
  private cdref = inject(ChangeDetectorRef);

  protected data = model<any>();
  protected readonly assets = assets;
  protected readonly documentType = '18748-4';

  protected documentContent: string | null = null;
  protected documentFileName: string | null = null;
  protected documentMimeType: string | null = null;

  protected medicalCentersList = [
    {
      id: 300001,
      name: '"ΠΡΟΓΝΩΣΗ" ΠΡΟΤΥΠΟ ΔΙΑΓΝΩΣΤΙΚΟ ΚΕΝΤΡΟ ΚΥΠΡΟΥ',
      tel: '22878000',
      email: 'info@prognosi.com.cy',
      street: 'Stasinou Avenue 6',
      postal_code: '2003',
      city: 'Strovolos, Nicosia',
      country: 'CY',
    },
    {
      id: 300002,
      name: 'A.G. X-RAY 4 U SERVICES LTD',
      tel: '25577070',
      email: 'info@xray4u.com.cy',
      street: 'Makariou III Avenue 195',
      postal_code: '3020',
      city: 'Limassol',
      country: 'CY',
    },
    {
      id: 300003,
      name: 'AIM DIAGNOSTIC CENTER LTD',
      tel: '22775500',
      email: 'info@aimdiagnostic.com',
      street: 'Athinon Avenue 12',
      postal_code: '1016',
      city: 'Nicosia',
      country: 'CY',
    },
    {
      id: 300004,
      name: 'F & D GALAZIS ESTATES LIMITED (ΠΟΛΥΚΛΙΝΙΚΗ ΛΕΥΚΩΣΙΑΣ)',
      tel: '22848800',
      email: 'info@nicosiapolyclinic.com',
      street: 'Andrea Avraamides 20',
      postal_code: '2024',
      city: 'Strovolos, Nicosia',
      country: 'CY',
    },
    {
      id: 300005,
      name: 'F.A.D. POLYCLINIC RADIO LAB LIMITED',
      tel: '22849000',
      email: 'radiology@nicosiapolyclinic.com',
      street: 'Andrea Avraamides 20',
      postal_code: '2024',
      city: 'Strovolos, Nicosia',
      country: 'CY',
    },
    {
      id: 300006,
      name: 'G. MARKIDES RADIOLOGY CENTER LIMITED',
      tel: '25586666',
      email: 'info@markidesradiology.com',
      street: 'Kolonakiou Avenue 89',
      postal_code: '4103',
      city: 'Germasogeia, Limassol',
      country: 'CY',
    },
    {
      id: 300007,
      name: 'G.C. EXPRESS X-RAY SERVICES LTD',
      tel: '24653333',
      email: 'info@gcexpressxray.com',
      street: 'Faneromenis Avenue 68',
      postal_code: '6025',
      city: 'Larnaca',
      country: 'CY',
    },
    {
      id: 300008,
      name: 'G.O.C. GERMAN ONCOLOGY CENTER LIMITED',
      tel: '25208000',
      email: 'info@goc.com.cy',
      street: 'Nikolaou Katalanou 22',
      postal_code: '3021',
      city: 'Limassol',
      country: 'CY',
    },
  ];

  protected form: FormGroup = new FormGroup({
    organizationId: new FormControl<number | undefined>(undefined, [Validators.required]),
    author: new FormControl<string>('', [Validators.required]),
    specialty: new FormControl<number | undefined>(undefined, [Validators.required]),
    hospitalization: new FormControl<number | undefined>(undefined, [Validators.required]),
    onsetDate: new FormControl<string>('', [Validators.required]),
    file: new FormControl<string>('', [Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  get file () {
    return this.form.get('file') as FormControl;
  }

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let organizationId = (control as FormGroup).controls['organizationId'];
      let author = (control as FormGroup).controls['author'];
      let specialty = (control as FormGroup).controls['specialty'];
      let hospitalization = (control as FormGroup).controls['hospitalization'];
      let onsetDate = (control as FormGroup).controls['onsetDate'];
      let file = (control as FormGroup).controls['file'];

      let patientDOB = moment(this.patientService.data?.dob, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'], true);
      let today = moment();

      if(organizationId.value === undefined || organizationId.value === null) {
        organizationId.setErrors({orcd_medicalCenterInvalid: true});
        errors['orcd_medicalCenterInvalid'] = true;
      }

      if(author.value === undefined || author.value === null || author.value === '') {
        author.setErrors({orcd_authorInvalid: true});
        errors['orcd_authorInvalid'] = true;
      }

      if(specialty.value === undefined || specialty.value === null) {
        specialty.setErrors({orcd_specialtyInvalid: true});
        errors['orcd_specialtyInvalid'] = true;
      }

      if(hospitalization.value === undefined || hospitalization.value === null) {
        hospitalization.setErrors({orcd_hospitalizationInvalid: true});
        errors['orcd_hospitalizationInvalid'] = true;
      }

      if((!checkDate(onsetDate.value).isValid())) {
        onsetDate.setErrors({da_onSetDateInvalid: true});
        errors['orcd_onSetDateInvalid'] = true;
      } else {
        if(checkDate(onsetDate.value).isBefore(patientDOB)) {
          onsetDate.setErrors({da_onSetDateAfterDOB: true});
          errors['orcd_onSetDateAfterDOB'] = true;
        } else if(checkDate(onsetDate.value).isSameOrAfter(today)) {
          onsetDate.setErrors({da_onSetDateBeforeToday: true});
          errors['orcd_onSetDateBeforeToday'] = true;
        } else {
          onsetDate.setErrors(null);
        }
      }

      if(file.value === undefined || file.value === null || file.value === '') {
        file.setErrors({orcd_fileInvalid: true});
        errors['orcd_hfileInvalid'] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  protected btnOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'add_medical_report',
      tooltip: 'add_medical_report',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'add_medical_report_modal',
      title: 'add_medical_report',
      type: ModalTypes.form,
      form: FormComponent,
      options: {
        animation: true,
        size: 'xl',
        fullscreen: 'lg',
        backdrop: 'static',
        keyboard: false,
      },
      //disableOnSave: false,
    })
  );

  protected medicalCenterOptions = signal<DropdownOptions<any>>(
    new DropdownOptions<any>({
      id: 'medicalReportOrganization',
      name: 'medicalReportOrganization',
      placeholder: 'select_medical_center',
      label: {
        id: 'medical_report_organization_label',
        text: 'medical_center',
      },
      data: this.medicalCentersList,
      dataId: 'id',
      descriptionKey: 'name',
      required: true,
      formControl: this.form.get('organizationId') as FormControl,
    })
  );

  protected authorInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'author_full_name',
      label: {
        text: 'author_full_name',
      },
      type: InputTypes.text,
      name: 'author_full_name',
      formControl: this.form.get('author') as FormControl,
      initialValue: '',
      required: true,
    })
  )

  protected specialtyOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'medicalReportSpecialty',
      name: 'medicalReportSpecialty',
      placeholder: 'specialty',
      label: {
        id: 'medical_report_specialty_label',
        text: 'specialty',
      },
      api: {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.101',
          description: '',
        },
      },
      required: true,
      searchComponent: DefaultSearchComponent,
      formControl: this.form.get('specialty') as FormControl,
    })
  );

  protected hospitalizationOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'medicalReportHospitalization',
      name: 'medicalReportHospitalization',
      placeholder: 'reason_of_hospitalization',
      label: {
        id: 'medical_report_hospitalization_label',
        text: 'reason_of_hospitalization',
      },
      api: {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.5',
          description: '',
        },
      },
      required: true,
      searchComponent: DefaultSearchComponent,
      formControl: this.form.get('hospitalization') as FormControl,
    })
  );

  protected dateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'medicalReportOnsetDate',
      label: {
        text: 'on_set',
      },
      name: 'medicalReportOnsetDate',
      initialValue: '',
      maxDate: moment().format('YYYY-MM-DD'),
      required: true,
    })
  );

  ngOnInit(): void {
    this.btnOptions().onClick = () => {
      this.mod.options().onReset = this.onFormReset;
      this.addMedicalReportButton.blur();
      this.mod.open();
    };

    this.dateOptions().formControl = this.form.get('onsetDate') as FormControl;
    this.modOptions().onSave = this.onSave;
    this.modOptions().onClose = this.onModalClose;
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    })

    return true;
  }

  protected onSave = (): void => {
    // if (this.modOptions().disableOnSave) {
    //   return;
    // }
    //
    // this.modOptions().disableOnSave = true;
    // this.mod.detectChanges();
    this.formEl?.ngSubmit.emit();
  };

  protected onFormReset = (): void => {
    // if (this.modOptions().disableOnSave) {
    //   return;
    // }
    //
    // this.modOptions().disableOnSave = true;
    // this.mod.detectChanges();
    this.resetForm();

    // setTimeout(() => {
    //   this.modOptions().disableOnSave = false;
    //   this.mod.detectChanges();
    // });
  };

  protected onModalClose = (): void => {
    this.resetForm();
    //this.modOptions().disableOnSave = false;
    this.mod.detectChanges();
  };

  private resetForm = (): void => {
    this.form.reset({
      organizationId: undefined,
      author: '',
      specialty: undefined,
      hospitalization: undefined,
      onsetDate: '',
      file: ''
    });

    this.documentContent = null;
    this.documentFileName = null;
    this.documentMimeType = null;

    if (this.documentFileInput) {
      this.documentFileInput.nativeElement.value = '';
    }

    this.medicalCenterDD?.clearSelection();
    this.specialtyDD?.clearSelection();
    this.hospitalizationDD?.clearSelection();
    this.onsetDate.reset();

    this.modOptions().errors = [];

    this.cdref.detectChanges();
  };

  protected onFileSelected = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      this.documentContent = null;
      this.documentFileName = null;
      this.documentMimeType = null;
      this.cdref.detectChanges();
      return;
    }

    if (file.type !== 'application/pdf') {
      input.value = '';
      this.documentContent = null;
      this.documentFileName = null;
      this.documentMimeType = null;
      this.cdref.detectChanges();
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result?.toString() || '';
      this.documentContent = result.split(',')[1] || null;
      this.documentFileName = file.name;
      this.documentMimeType = file.type;
      this.cdref.detectChanges();
    };

    reader.onerror = () => {
      input.value = '';
      this.documentContent = null;
      this.documentFileName = null;
      this.documentMimeType = null;
      this.cdref.detectChanges();
    };

    reader.readAsDataURL(file);
  };

  protected onFormSubmit = (): void => {
    console.log(this.form.value || !this.documentContent);
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    const center = this.medicalCentersList.find(
      (c) => c.id === this.form.get('organizationId')?.value
    );

    if (!center) {
      //this.modOptions().disableOnSave = false;
      //this.mod.detectChanges();
      return;
    }

    const hcp = this.getHcp();
    const onsetDateValue = this.form.get('onsetDate')?.value;

    const payload: any = {
      status: 1,
      documentType: this.documentType,
      document: this.documentContent,
      onsetDate: this.formatDateForOrcd(onsetDateValue),
      hcpName: hcp.hcpName,
      hcpSurname: hcp.hcpSurname,
      author: this.form.get('author')?.value,
      specialty: this.specialtyOptions().value?.description,
      reason_of_hospitalization_coding_id:
        this.hospitalizationOptions().value?.coding_id?.toString(),
      organizationId: center.id.toString(),
      organizationName: center.name,
      organizationTel: center.tel,
      organizationEmail: center.email,
      organizationStreet: center.street,
      organizationPostalCode: center.postal_code,
      organizationCity: center.city,
      organizationCountry: center.country,
    };

    this.apiService
      .call({
        path: '/document',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        type: HTTPMethods.POST,
        body: payload,
      })
      ?.subscribe({
        next: (res: any) => {
          if (res && res.status?.toString() === '1') {
            const tableData = this.data()?.tblOptions().data || [];

            const row: MedicalReport = res.data || {
              id: res.id,
              orcd_document_id: res.orcd_document_id,
              onset_date: this.formatDateForTable(onsetDateValue),
              hcp_name: hcp.hcpName,
              hcp_surname: hcp.hcpSurname,
              organization_name: center.name,
              status: 1,
              document_type: this.documentType,
              preview: '',
            };

            tableData.unshift(row);

            if (this.data()?.tbl?.tableData) {
              this.data().tbl.tableData = tableData;
            }

            //this.modOptions().disableOnSave = false;
            this.mod.detectChanges();
            this.mod.close();
            return;
          }

          //this.modOptions().disableOnSave = false;
          this.mod.detectChanges();
        },
        error: () => {
          //this.modOptions().disableOnSave = false;
          this.mod.detectChanges();
        },
      });
  };

  private formatDateForOrcd = (value?: string | Date | null): string | null => {
    if (!value) {
      return null;
    }

    if (typeof value === 'string' && value.includes('-')) {
      const parts = value.split('-');

      if (parts[0].length === 4) {
        return `${parts[0]}-${parts[1]}-${parts[2]} 00:00:00`;
      }

      if (parts[2]?.length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]} 00:00:00`;
      }
    }

    const date = new Date(value);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day} 00:00:00`;
  };

  private formatDateForTable = (value?: string | Date | null): string => {
    if (!value) {
      return '';
    }

    if (typeof value === 'string' && value.includes('-')) {
      const parts = value.split('-');

      if (parts[0].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }

      if (parts[2]?.length === 4) {
        return value;
      }
    }

    const date = new Date(value);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  private getHcp = (): {
    hcpName: string;
    hcpSurname: string;
  } => {
    const profile = JSON.parse(sessionStorage.getItem('profile') || '{}');
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser') || '{}');
    const token = profile?.jwt || profile?.token || currentUser?.token;
    const decoded = this.decodeJwt(token);

    return {
      hcpName: decoded?.given_name || profile?.given_name || '',
      hcpSurname: decoded?.family_name || profile?.family_name || '',
    };
  };

  private decodeJwt = (token?: string): any => {
    return token ? decodeToken(token) : {};
  };
}
